// The structured email model for the block builder: an ordered list of blocks
// the author composes (header, text, button, image, divider, spacer, footer).
// The block types, the brand tokens and the renderer live in the SHARED
// `@notifi/shared-types/email-layout` (the backend send renders the same code),
// so what the builder previews is what the customer receives. This module adds
// the builder-side helpers: fresh blocks, starter layouts, legacy migration.
import {
  KIND_CTA,
  type EmailAlign,
  type EmailBlock,
  type EmailBlockType,
  type EmailBranding,
  type EmailBrandingOverride,
  type EmailButtonBlock,
  type EmailFooterBlock,
  type EmailHeaderBlock,
  type EmailLayout,
  type EmailLegalBlock,
  type EmailTextBlock,
} from '@notifi/shared-types/email-layout';
import {
  EMAIL_KINDS,
  defaultEmailLayout as registryDefaultLayout,
} from '@notifi/shared-types/email-kinds';
import type { EmailTemplateKind } from '@/types/emailTemplate';

export type {
  EmailAlign,
  EmailBlock,
  EmailBlockType,
  EmailBranding,
  EmailBrandingOverride,
  EmailButtonBlock,
  EmailFooterBlock,
  EmailHeaderBlock,
  EmailLayout,
  EmailLegalBlock,
  EmailTextBlock,
};
export { KIND_CTA };

let seq = 0;
/** A short client-side id for a new block (stable within a session). */
export function newBlockId(type: EmailBlockType): string {
  seq += 1;
  return `${type}-${seq}`;
}

/**
 * A sensible fresh block of `type`, ready to drop into the layout.
 *
 * `kind` decides what a new BUTTON points at. Without it every button was
 * seeded `estimateView`, so adding one to an invoice email produced
 * `{{invoice.payUrl}}`'s wrong twin — `{{estimate.viewUrl}}` — which the
 * preview cannot resolve and publish rejects as unsupported for the kind. The
 * author's only clue was a rejected publish naming a token they never typed.
 * Defaults to the estimate CTA for the callers that have no kind in hand
 * (legacy-HTML migration seeds no button at all).
 */
export function makeBlock(type: EmailBlockType, kind?: EmailTemplateKind): EmailBlock {
  const id = newBlockId(type);
  switch (type) {
    case 'header':
      return { id, type, showLogo: true, html: '<p>{{business.displayName}}</p>', align: 'left' };
    case 'text':
      return { id, type, html: '<p>Write your message…</p>' };
    case 'button': {
      const cta = KIND_CTA[kind ?? 'ESTIMATE_SEND'];
      // A kind with no link of its own gets a custom-URL button.
      return cta
        ? { id, type, label: cta.label, action: cta.action, url: '', align: 'left' }
        : { id, type, label: 'Open', action: 'custom', url: '', align: 'left' };
    }
    case 'legal':
      // Only ever born from the factory default (locked, not addable); the
      // token is the kind's required legal text.
      return {
        id,
        type,
        token: (kind && EMAIL_KINDS[kind].legal) || 'legal.financing',
        locked: true,
      };
    case 'image':
      return { id, type, src: '{{business.logo}}', align: 'center' };
    case 'divider':
      return { id, type };
    case 'spacer':
      return { id, type, size: 'md' };
    case 'footer':
      return {
        id,
        type,
        html:
          '<p>{{business.displayName}}</p><p>{{business.address}}</p>' +
          '<p>{{business.phone}} · {{business.email}}</p>',
      };
  }
}

/**
 * Whether a block is the kind's call-to-action — the customer's only way to
 * open the document / manage the visit. The builder will not delete it or
 * change what it points at; the server refuses to publish a design without it.
 */
export function isKindCtaBlock(block: EmailBlock, kind: EmailTemplateKind): boolean {
  const cta = KIND_CTA[kind];
  return Boolean(cta) && block.type === 'button' && block.action === cta?.action;
}

/**
 * Bring a stored layout up to the current block shape — header/footer used to be
 * toggle blocks (showName/showAddress/…) before they became editable rich text.
 * Seeds `html` where missing, preserving the header's logo/align choice.
 */
export function migrateLayout(layout: EmailLayout): EmailLayout {
  return {
    blocks: layout.blocks.map((b) => {
      if (b.type === 'header' && typeof (b as { html?: unknown }).html !== 'string') {
        const seed = makeBlock('header') as EmailHeaderBlock;
        const old = b as unknown as { showLogo?: boolean; align?: EmailAlign };
        return {
          ...seed,
          id: b.id,
          showLogo: old.showLogo ?? seed.showLogo,
          align: old.align ?? seed.align,
        };
      }
      if (b.type === 'footer' && typeof (b as { html?: unknown }).html !== 'string') {
        return { ...(makeBlock('footer') as EmailFooterBlock), id: b.id };
      }
      return b;
    }),
  };
}

/**
 * Seed a layout for an email that has no stored `layout` yet — a legacy email
 * authored as raw `bodyHtml` (the old rich-text editor, or a backend-seeded
 * default). The saved HTML is preserved verbatim in a text block, so opening a
 * legacy email in the block builder and saving never discards the author's
 * content (it would otherwise be replaced by the kind's starter). Falls back to
 * the starter layout only when there is genuinely no body to preserve.
 *
 * A legacy email relied on the send's automatic shell for its business-name
 * header and contact footer; once it carries a `layout` the send renders the
 * blocks instead. So restore that header + footer as real blocks around the
 * preserved body — otherwise migrating a legacy email would silently drop its
 * header/footer from what the customer receives. Its CTA stays the link inside
 * the preserved text (publish accepts that form).
 */
export function layoutFromLegacyHtml(
  bodyHtml: string | null | undefined,
  kind: EmailTemplateKind,
): EmailLayout {
  const html = (bodyHtml ?? '').trim();
  if (!html) return defaultEmailLayout(kind);
  const legal = EMAIL_KINDS[kind].legal;
  return {
    blocks: [
      makeBlock('header'),
      { ...(makeBlock('text') as EmailTextBlock), html },
      // The kind's required legal block (publish demands it).
      ...(legal ? [makeBlock('legal', kind) as EmailLegalBlock] : []),
      makeBlock('footer'),
    ],
  };
}

/**
 * The factory layout for a kind — header, greeting, locked CTA, closing,
 * locked legal block, footer — from the shared registry (the send renders the
 * same one when the tenant has no published design).
 */
export function defaultEmailLayout(kind: EmailTemplateKind): EmailLayout {
  return registryDefaultLayout(kind);
}
